import { Component, h, Prop, State, Watch } from '@stencil/core';

interface Contact {
    _id: string;
    fs_date: number;
    fs_project_id: number;
    fs_project_name: string;
    fs_revision_id: number;
    fs_reference_name: string;
    fs_object_type: string;
    fs_metadata: unknown;
    fs_uid_type: string;
    fs_scheduler_date: number;
    fs_id: string;
    fs_language: string;
    apiBaseUrl: string;
    externalZipcodeSearch: string;
    externalZipcodeInputName: string;
    apiKey: string;
    localization: { language: string; region: string };
    cardTitle: string;
    firstPhoneNumber: string;
    firstPhoneTitle: string;
    firstPhoneHelpText: string;
    secondPhoneNumber: string;
    secondPhoneTitle: string;
    secondPhoneHelpText: string;
    thirdPhoneNumber: string;
    thirdPhoneTitle: string;
    thirdPhoneHelpText: string;
    placeholder: string;
    links: { link: Link; tracking: Tracking }[];
    ariaLabelDownload: string;
    ariaLabelContact: string;
    ariaLabelScrollToTop: string;
    chatBtnTitle: string;
    chatBtnText: string;
    downloads: {
        downloadText: string;
        downloadTitle: string;
        buttonText: string;
        linksHeadline: string;
        linksText: string;
        downloadTypes: DownloadType[];
    };
    whatsapp: {
        headline: string;
        items: Array<{
            text?: string;
            img?: {
                src: string;
                alt: string;
                title: string;
            };
        }>;
    };
    searchInputLabel: string;
    tipLabel: string;
    tipOptOutLabel: string;
    tipDuration: number;
    tipApiUrl: string;
}

interface Link {
    fragment: string;
    href: string;
    target: string;
    text: string;
    title: string;
}

interface Tracking {
    action: string;
    category: string;
    object: string;
}

interface DownloadType {
    name: string;
    value: string;
}

@Component({
    tag: 'sew-ws-floating-buttons-api',
    shadow: true,
})
export class SewWsFloatingButtonsApi {
    @Prop() contactApiUrl: string;
    @Prop() linkBtnText: string;
    @Prop() linkBtnLink: string;
    @Prop() linkBtnIcon: string;
    @Prop() enableDownloads: boolean;
    @Prop() enableTips: boolean;
    @Prop() enableLinkButton: boolean;
    @Prop() enableIdleAnimation: boolean;

    @State() data: Contact;
    @State() enableWhatsapp: boolean;

    @Watch('contactApiUrl')
    updateData(): void {
        this.fetchData();
    }

    private fetchData = async () => {
        this.contactApiUrl.length > 0 &&
            (await fetch(this.contactApiUrl)
                .then(
                    res => res.json(),
                    err => console.error(err)
                )
                .then(json => {
                    this.data = json['_embedded']['rh:doc'][0];

                    this.enableWhatsapp = !!this.data.whatsapp;
                })
                .catch(err => {
                    console.error(err);
                }));
    };

    componentWillLoad(): void {
        this.fetchData();
    }

    render() {
        return (
            this.data && (
                <sew-ws-floating-buttons
                    card-title={this.data.cardTitle}
                    first-phone-title={this.data.firstPhoneTitle}
                    first-phone-number={this.data.firstPhoneNumber}
                    first-phone-help-text={this.data.firstPhoneHelpText}
                    second-phone-title={this.data.secondPhoneTitle}
                    second-phone-number={this.data.secondPhoneNumber}
                    second-phone-help-text={this.data.secondPhoneHelpText}
                    third-phone-title={this.data.thirdPhoneTitle}
                    third-phone-number={this.data.thirdPhoneNumber}
                    third-phone-help-text={this.data.thirdPhoneHelpText}
                    link-btn-text={this.linkBtnText}
                    link-btn-icon={this.linkBtnIcon}
                    link-btn-link={this.linkBtnLink}
                    api-key={this.data.apiKey}
                    localization={this.data.localization}
                    api-base-url={this.data.apiBaseUrl}
                    external-zipcode-search={this.data.externalZipcodeSearch}
                    external-zipcode-input-name={this.data.externalZipcodeInputName}
                    placeholder={this.data.placeholder}
                    tip-api-url={this.data.tipApiUrl}
                    tip-duration={this.data.tipDuration}
                    tip-opt-out-label={this.data.tipOptOutLabel}
                    tip-label={this.data.tipLabel}
                    enable-downloads={this.enableDownloads}
                    enable-whatsapp={this.enableWhatsapp}
                    enable-tips={this.enableTips}
                    enable-link-button={this.enableLinkButton}
                    enable-idle-animation={this.enableIdleAnimation}
                    contact-aria-label={this.data.ariaLabelContact}
                    download-aria-label={this.data.ariaLabelDownload}
                    scroll-to-top-aria-label={this.data.ariaLabelScrollToTop}
                    chat-btn-title={this.data.chatBtnTitle}
                    chat-btn-text={this.data.chatBtnText}
                >
                    {this.data.links.map(item => (
                        <sew-ws-link slot="links">
                            <a
                                href={item.link.href}
                                target={item.link.target}
                                data-sew-trackme-event-category={item.tracking.category}
                                data-sew-trackme-event-object={item.tracking.object}
                                data-sew-trackme-event-action={item.tracking.action}
                            >
                                {item.link.text}
                            </a>
                        </sew-ws-link>
                    ))}

                    {this.enableDownloads && (
                        <sew-ws-downloads
                            slot="download"
                            download-title={this.data.downloads.downloadTitle}
                            download-types={JSON.stringify(this.data.downloads.downloadTypes)}
                            button-text={this.data.downloads.buttonText}
                            links-headline={this.data.downloads.linksHeadline}
                            links-text={this.data.downloads.linksText}
                            download-text={this.data.downloads.downloadText}
                            search-input-label={this.data.searchInputLabel}
                            tracking-category="Quicknavi"
                            services-item-label="Data and Documents"
                            allow-search-input
                            class="inside-flyout"
                        />
                    )}
                    {this.enableWhatsapp && (
                        <sew-ws-whatsapp-service
                            slot="whatsapp"
                            headline={this.data.whatsapp.headline}
                            items={this.data.whatsapp.items}
                        ></sew-ws-whatsapp-service>
                    )}
                </sew-ws-floating-buttons>
            )
        );
    }
}
